import {
  AgentChatMessageId,
  AgentChatRunId,
  AgentChatThreadId,
  DateTimeString,
  HexId,
  HexVersionId,
  UserId,
  stableEmptyArray,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { sortBy } from "lodash";

import { AgentChatMessageFragment } from "../../components/agent/hex-project-shared/messages/AgentChatMessage.generated";
import {
  AgentChatMessage,
  AgentChatThread,
  AgentRunCreditSpend,
} from "../../components/agent/shared/agentChatMessage.types";
import {
  messageFragmentToAgentChatMessage,
  threadFragmentToAgentChatThread,
} from "../../components/agent/shared/agentChatMessageTypeUtils";
import { mergeAgentChatMessagesWithRevisions } from "../../components/agent/shared/mergeAgentChatMessagesWithRevisions";
import {
  AgentChatThreadFragment,
  AgentChatThreadListItemFragment,
} from "../../components/agent/shared/sharedThreadManagementUtils.generated";
import {
  AgentChatThreadStatus,
  AgentChatThreadType,
} from "../../generated/graphqlTypes";
import { RootState } from "../store";
import { createShallowResultSelector } from "../utils/index.js";

type AgentChatState = {
  /**
   * A lightweight list of agent chat threads without messages or edits.
   */
  agentChatThreadsMap: Record<AgentChatThreadId, AgentChatThread>;
  /**
   * True when the initial page of agent chat threads is loading.
   */
  isLoadingAgentChatThreads: boolean;
  /**
   * True when there are more threads that can be loaded via a paginated query.
   */
  hasMoreThreads: boolean;
  /**
   * True when the next page of threads for the thread list is loading.
   */
  isLoadingMoreChatThreads: boolean;
  /**
   * The id of the currently selected thread.
   */
  selectedThreadId: AgentChatThreadId | null;
  /**
   * True when the full details of the selected thread are loading.
   */
  isLoadingSelectedThread: boolean;
  /**
   * Incremented to trigger loading more threads. Components can dispatch
   * triggerLoadMoreThreads to increment this value and useAgentChatThreads
   * will respond by loading the next page.
   */
  loadMoreThreadsTrigger: number;
  /**
   * A record of live credit spend updates keyed by agent run.
   */
  agentRunCreditSpend: Record<AgentChatRunId, AgentRunCreditSpend>;
  /**
   * Threads whose background autosave version is still being created. Kept
   * separate from the thread objects since those get rebuilt by fetches.
   * Cleared when the thread's autoSaveHexVersionId arrives or the autosave fails.
   */
  autoSavePendingThreadIds: Record<AgentChatThreadId, true>;
  /**
   * Threads whose background autosave failed. Kept separate from the thread
   * objects so a failure subscription that beats the mutation response still
   * prevents the mutation response from re-marking the thread as pending.
   */
  autoSaveFailedThreadIds: Record<AgentChatThreadId, true>;
  /**
   * Ask-user question messages the user has dismissed locally. The card and the
   * "waiting for approval" state are hidden as soon as a messageId lands here,
   * so the user is never trapped even if the backend never resolves the
   * question (e.g. the agent worker restarted and dropped the response).
   * Cleared on hex navigation via `resetForNewHex`; ids are unique so
   * within-session growth is bounded and harmless.
   */
  dismissedAskUserMessageIds: Record<AgentChatMessageId, true>;
};

const initialState: AgentChatState = {
  agentChatThreadsMap: {},
  isLoadingAgentChatThreads: false,
  hasMoreThreads: false,
  isLoadingMoreChatThreads: false,
  selectedThreadId: null,
  isLoadingSelectedThread: false,
  loadMoreThreadsTrigger: 0,
  agentRunCreditSpend: {},
  autoSavePendingThreadIds: {},
  autoSaveFailedThreadIds: {},
  dismissedAskUserMessageIds: {},
};

/**
 * Manages agent chat thread state including thread list, selection, and messages.
 */
export const agentChatSlice = createSlice({
  name: "agentChat",
  initialState,
  reducers: {
    resetForNewHex: (
      _state: AgentChatState,
      action: PayloadAction<{ selectedThreadId: AgentChatThreadId | null }>,
    ) => {
      // Reset all state for new hex, but preserve the stored thread selection
      return {
        ...initialState,
        selectedThreadId: action.payload.selectedThreadId,
      };
    },
    setAgentChatThreads: (
      state: AgentChatState,
      action: PayloadAction<readonly AgentChatThreadListItemFragment[]>,
    ) => {
      const threads: AgentChatThread[] = action.payload.map(
        threadFragmentToAgentChatThread,
      );

      // Reset and rebuild the threads map
      state.agentChatThreadsMap = {};

      for (const thread of threads) {
        state.agentChatThreadsMap[thread.id] = thread;
      }
    },
    appendAgentChatThreads: (
      state: AgentChatState,
      action: PayloadAction<readonly AgentChatThreadListItemFragment[]>,
    ) => {
      const threads: AgentChatThread[] = action.payload.map(
        threadFragmentToAgentChatThread,
      );

      for (const thread of threads) {
        const existing = state.agentChatThreadsMap[thread.id];
        state.agentChatThreadsMap[thread.id] =
          existing != null
            ? {
                ...thread,
                agentMessages: existing.agentMessages,
                messageCount: Math.max(
                  thread.messageCount,
                  existing.messageCount,
                  existing.agentMessages.length,
                ),
                startCursor: existing.startCursor,
                hasEarlierMessages: existing.hasEarlierMessages,
              }
            : thread;
      }
    },
    setHasMoreThreads: (
      state: AgentChatState,
      action: PayloadAction<boolean>,
    ) => {
      state.hasMoreThreads = action.payload;
    },
    setIsLoadingAgentChatThreads: (
      state: AgentChatState,
      action: PayloadAction<boolean>,
    ) => {
      state.isLoadingAgentChatThreads = action.payload;
    },
    setIsLoadingMoreChatThreads: (
      state: AgentChatState,
      action: PayloadAction<boolean>,
    ) => {
      state.isLoadingMoreChatThreads = action.payload;
    },
    triggerLoadMoreThreads: (state: AgentChatState) => {
      state.loadMoreThreadsTrigger = state.loadMoreThreadsTrigger + 1;
    },
    setSelectedThreadId: (
      state: AgentChatState,
      action: PayloadAction<AgentChatThreadId | null>,
    ) => {
      if (action.payload === state.selectedThreadId) {
        return;
      }

      state.selectedThreadId = action.payload;
      // Nothing to load if the thread id is null
      state.isLoadingSelectedThread = action.payload != null;
    },
    /**
     * Sets messages for a thread.
     * Subagent messages are now stored in the same thread with subagentType set on the message.
     */
    setMessagesForThread: (
      state: AgentChatState,
      action: PayloadAction<{
        thread: AgentChatThreadFragment;
        currentUserId: UserId | null;
      }>,
    ) => {
      const { currentUserId, thread } = action.payload;

      const messages = thread.agentMessages.edges.map((e) => e.node);
      setThreadMessages(state, {
        threadId: thread.id,
        messages,
        currentUserId,
      });

      // Carry the server-side truncation signal through so the UI can render
      // an "earlier messages hidden" banner. False when the full thread was
      // returned (the common case).
      const storedThread = state.agentChatThreadsMap[thread.id];
      if (storedThread != null) {
        storedThread.auto = thread.auto;
        storedThread.model = thread.model;
        storedThread.lastUsedEffortLevel = thread.lastUsedEffortLevel;
        storedThread.lastUsedSpeed = thread.lastUsedSpeed;
        storedThread.messageCount = Math.max(
          thread.messageCount,
          storedThread.messageCount,
          storedThread.agentMessages.length,
        );
        storedThread.hasEarlierMessages =
          thread.agentMessages.pageInfo?.hasPreviousPage ?? false;
        // Preserve a cursor set by a prior prependMessagesForThread — re-fetching
        // the thread (e.g. on re-select) returns only the latest page and would
        // otherwise regress the cursor, breaking subsequent pagination.
        storedThread.startCursor =
          storedThread.startCursor ??
          thread.agentMessages.pageInfo?.startCursor ??
          null;
      }

      state.isLoadingSelectedThread = false;
    },
    prependMessagesForThread: (
      state: AgentChatState,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        messages: readonly AgentChatMessageFragment[];
        hasPreviousPage: boolean;
        startCursor: string | null;
      }>,
    ) => {
      const { hasPreviousPage, messages, startCursor, threadId } =
        action.payload;
      const thread = state.agentChatThreadsMap[threadId];
      if (thread == null) return;

      const olderMessages = messages.map(messageFragmentToAgentChatMessage);
      const threadCopy = { ...thread };
      threadCopy.agentMessages = mergeAgentChatMessagesWithRevisions(
        olderMessages,
        threadCopy.agentMessages,
      );
      threadCopy.hasEarlierMessages = hasPreviousPage;
      threadCopy.startCursor = startCursor;
      threadCopy.messageCount = Math.max(
        threadCopy.messageCount,
        threadCopy.agentMessages.length,
      );
      state.agentChatThreadsMap[threadId] = threadCopy;
    },
    setIsLoadingSelectedThread: (
      state: AgentChatState,
      action: PayloadAction<boolean>,
    ) => {
      state.isLoadingSelectedThread = action.payload;
    },
    addMessagesToThread: (
      state: AgentChatState,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        messages: readonly AgentChatMessageFragment[];
        currentUserId: UserId | null;
        /**
         * If the caller knows the thread's hex (e.g. a hex-scoped
         * subscription), pass it so any optimistic placeholder this
         * action creates has the correct `hexId` immediately. Without
         * this, hex-id filters miss the thread until the full thread
         * fragment arrives.
         */
        hexId?: HexId | null;
      }>,
    ) => {
      const { currentUserId, hexId, messages, threadId } = action.payload;

      const thread = ensureThreadExists(state, threadId, currentUserId, hexId);

      // Check if these are subagent messages (have subagentType set)
      const hasSubagentMessages = messages.some((m) => m.subagentType != null);

      // Skip adding messages if user explicitly cancelled (IDLE_STOP_REQUESTED)
      // This prevents lagging messages from appearing after user clicks stop.
      // Normal IDLE (agent completed naturally) should still accept messages.
      // BUT allow subagent messages to continue streaming even after
      // the thread status becomes IDLE_STOP_REQUESTED, since the parent thread may still be active
      const existingThread = state.agentChatThreadsMap[threadId];
      if (
        existingThread?.status === "IDLE_STOP_REQUESTED" &&
        !hasSubagentMessages
      ) {
        return;
      }

      const threadCopy = { ...thread };
      const newMessages: AgentChatMessage[] = messages.map(
        messageFragmentToAgentChatMessage,
      );

      // Combine existing messages and new messages, deduplicating by staticId
      threadCopy.agentMessages = mergeAgentChatMessagesWithRevisions(
        threadCopy.agentMessages,
        newMessages,
      );
      threadCopy.messageCount = Math.max(
        threadCopy.messageCount,
        threadCopy.agentMessages.length,
      );

      state.agentChatThreadsMap[threadId] = threadCopy;
    },
    updateThreadMetadata: (
      state: AgentChatState,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        updates: {
          title: string | null;
          status: AgentChatThreadStatus;
          autoSaveHexVersionId: HexVersionId | null;
          autoSaveFailed?: boolean | null;
        };
        currentUserId: UserId | null;
        /**
         * If the caller knows the thread's hex (e.g. a hex-scoped
         * subscription), pass it so a still-null hexId on an existing
         * optimistic thread gets backfilled immediately.
         */
        hexId?: HexId | null;
      }>,
    ) => {
      const { hexId, threadId, updates } = action.payload;
      const existingThread = state.agentChatThreadsMap[threadId];
      if (existingThread == null) {
        return;
      }
      const thread =
        existingThread.hexId == null && hexId != null
          ? { ...existingThread, hexId }
          : existingThread;

      // Make IDLE_STOP_REQUESTED "sticky" - if user stopped the thread, ignore
      // incoming IDLE from workflow completion to prevent lagging messages from
      // being accepted. Also avoid moving backwards for status updates that only
      // attach the autosave version after the run has already started or ended.
      const existingStatus = thread.status;
      const isAutosaveVersionUpdate =
        (updates.autoSaveHexVersionId != null ||
          updates.autoSaveFailed === true) &&
        updates.title == null;
      const newStatus =
        existingStatus === "IDLE_STOP_REQUESTED" && updates.status === "IDLE"
          ? "IDLE_STOP_REQUESTED"
          : existingStatus === "RUNNING" && updates.status === "SAVING_VERSION"
            ? "RUNNING"
            : (existingStatus === "IDLE" ||
                  existingStatus === "IDLE_STOP_REQUESTED") &&
                updates.status === "RUNNING" &&
                isAutosaveVersionUpdate
              ? existingStatus
              : updates.status;

      const threadCopy = { ...thread, status: newStatus };

      if (updates.title) {
        threadCopy.title = updates.title;
      }

      if (updates.autoSaveHexVersionId) {
        threadCopy.autoSaveHexVersionId = updates.autoSaveHexVersionId;
        delete state.autoSaveFailedThreadIds[threadId];
      }

      if (updates.autoSaveFailed === true) {
        state.autoSaveFailedThreadIds[threadId] = true;
      }

      if (
        (isAutosaveVersionUpdate ||
          updates.status === "IDLE" ||
          updates.status === "IDLE_STOP_REQUESTED") &&
        state.autoSavePendingThreadIds != null
      ) {
        delete state.autoSavePendingThreadIds[threadId];
      }

      state.agentChatThreadsMap[threadId] = threadCopy;
    },
    setThreadAutoSavePending: (
      state: AgentChatState,
      action: PayloadAction<{ threadId: AgentChatThreadId }>,
    ) => {
      const { threadId } = action.payload;
      // If the autosave already completed (the subscription update can beat the
      // mutation response), or already failed, don't re-mark the thread as pending.
      if (
        state.agentChatThreadsMap[threadId]?.autoSaveHexVersionId == null &&
        state.autoSaveFailedThreadIds[threadId] !== true
      ) {
        state.autoSavePendingThreadIds ??= {};
        state.autoSavePendingThreadIds[threadId] = true;
      }
    },
    updateThreadIsSensitive: (
      state: AgentChatState,
      action: PayloadAction<{
        threadId: AgentChatThreadId;
        sensitive: boolean;
      }>,
    ) => {
      const { sensitive, threadId } = action.payload;
      const thread = state.agentChatThreadsMap?.[threadId];
      if (!thread) return;
      thread.sensitive = sensitive;
    },
    removeThread: (
      state: AgentChatState,
      action: PayloadAction<{ threadId: AgentChatThreadId }>,
    ) => {
      const { threadId } = action.payload;
      delete state.agentChatThreadsMap[threadId];
    },
    addRunCreditSpend: (
      state: AgentChatState,
      action: PayloadAction<AgentRunCreditSpend>,
    ) => {
      state.agentRunCreditSpend[action.payload.runId] = action.payload;
    },
    dismissAskUserMessage: (
      state: AgentChatState,
      action: PayloadAction<AgentChatMessageId>,
    ) => {
      state.dismissedAskUserMessageIds[action.payload] = true;
    },
    undismissAskUserMessage: (
      state: AgentChatState,
      action: PayloadAction<AgentChatMessageId>,
    ) => {
      delete state.dismissedAskUserMessageIds[action.payload];
    },
  },
});

const selectThreads = createShallowResultSelector(
  [(state: RootState) => state.agentChat.agentChatThreadsMap],
  (threadsMap) => sortBy(Object.values(threadsMap), "createdDate").reverse(),
);

/**
 * Returns a thread's messages sorted chronologically by createdDate.
 * Subagent messages are now stored in the same thread with subagentType set on the message.
 */
const selectThreadMessages = (
  state: RootState,
  threadId: AgentChatThreadId | null,
): readonly AgentChatMessage[] => {
  if (!threadId) return stableEmptyArray<AgentChatMessage>();

  const thread = state.agentChat.agentChatThreadsMap[threadId];
  if (!thread) return stableEmptyArray<AgentChatMessage>();

  return thread.agentMessages;
};

export const agentChatSelectors = {
  selectIsLoadingThreads: (state: RootState) =>
    state.agentChat.isLoadingAgentChatThreads,
  selectIsLoadingSelectedThread: (state: RootState) =>
    state.agentChat.isLoadingSelectedThread,
  selectSelectedThreadId: (state: RootState) =>
    state.agentChat.selectedThreadId,
  selectIsLoadingMoreChatThreads: (state: RootState) =>
    state.agentChat.isLoadingMoreChatThreads,
  selectHasMoreThreads: (state: RootState) => state.agentChat.hasMoreThreads,
  selectLoadMoreThreadsTrigger: (state: RootState) =>
    state.agentChat.loadMoreThreadsTrigger,

  selectThreads,
  selectHasThreads: (state: RootState) => selectThreads(state).length > 0,

  selectSelectedThread: (state: RootState) => {
    const selectedThreadId = state.agentChat.selectedThreadId;
    return selectedThreadId
      ? state.agentChat.agentChatThreadsMap[selectedThreadId] || null
      : null;
  },

  selectIsSelectedThreadRunningOrSaving: (state: RootState) => {
    const selectedThreadId = state.agentChat.selectedThreadId;
    if (selectedThreadId == null) return false;

    const selectedThread =
      state.agentChat.agentChatThreadsMap[selectedThreadId];
    return (
      selectedThread?.status === "RUNNING" ||
      selectedThread?.status === "SAVING_VERSION"
    );
  },

  /**
   * Returns the selected thread's messages.
   * Subagent messages are included in the thread with subagentType set on the message.
   */
  selectSelectedThreadMessages: (state: RootState) => {
    const selectedThreadId = state.agentChat.selectedThreadId;
    return selectThreadMessages(state, selectedThreadId);
  },

  selectRunCreditSpendByRunId: (
    state: RootState,
    runId: AgentChatRunId | undefined,
  ) =>
    runId != null ? (state.agentChat.agentRunCreditSpend[runId] ?? null) : null,

  selectIsSelectedThreadAutoSavePending: (state: RootState) => {
    const selectedThreadId = state.agentChat.selectedThreadId;
    if (selectedThreadId == null) return false;
    return (
      state.agentChat.autoSavePendingThreadIds?.[selectedThreadId] === true
    );
  },
  selectHasThreadAutoSaveFailed: (
    state: RootState,
    threadId: AgentChatThreadId,
  ) => state.agentChat.autoSaveFailedThreadIds[threadId] === true,

  selectDismissedAskUserMessageIds: (state: RootState) =>
    state.agentChat.dismissedAskUserMessageIds,
};

export const agentChatReducer = agentChatSlice.reducer;

export const {
  addMessagesToThread,
  addRunCreditSpend,
  appendAgentChatThreads,
  dismissAskUserMessage,
  prependMessagesForThread,
  removeThread,
  resetForNewHex,
  setAgentChatThreads,
  setHasMoreThreads,
  setIsLoadingAgentChatThreads,
  setIsLoadingMoreChatThreads,
  setIsLoadingSelectedThread,
  setMessagesForThread,
  setSelectedThreadId,
  setThreadAutoSavePending,
  triggerLoadMoreThreads,
  undismissAskUserMessage,
  updateThreadIsSensitive,
  updateThreadMetadata,
} = agentChatSlice.actions;

/**
 * Creates an optimistic thread when we receive updates for a thread that
 * doesn't exist locally. This can happen when we get real-time updates before
 * the thread list has been loaded.
 *
 * NOTE: This function assumes that any optimistically created threads belong to
 * the currently logged-in user.
 */
const createOptimisticThread = (
  threadId: AgentChatThreadId,
  currentUserId: UserId | null,
  hexId: HexId | null,
): AgentChatThread => {
  return {
    id: threadId,
    hexId,
    hexVersionId: null,
    title: null,
    createdDate: new Date().toISOString() as DateTimeString,
    status: "IDLE" as AgentChatThreadStatus,
    // Optimistic placeholder, overwritten when the real thread fragment
    // lands. Wrong for published-app Ask callers (real type is `ASK`).
    type: "LOGIC_VIEW_V2" as AgentChatThreadType,
    autoSaveHexVersionId: null,
    agentMessages: [],
    editsToReview: [],
    userId: currentUserId,
    messageCount: 0,
    auto: false,
    model: null,
    lastUsedEffortLevel: null,
    lastUsedSpeed: null,
    startCursor: null,
  };
};

/**
 * Ensures a thread exists in the state, creating it optimistically if needed.
 * Returns the thread (existing or newly created). Callers that know which
 * hex a message belongs to (e.g. the hex-scoped subscription) should pass
 * `hexId` so the optimistic placeholder is matchable by hex-id filters
 * before the full thread fragment arrives.
 */
const ensureThreadExists = (
  state: AgentChatState,
  threadId: AgentChatThreadId,
  currentUserId: UserId | null,
  hexId: HexId | null = null,
): AgentChatThread => {
  let thread = state.agentChatThreadsMap[threadId];

  if (!thread) {
    thread = createOptimisticThread(threadId, currentUserId, hexId);
    state.agentChatThreadsMap[threadId] = thread;
  } else if (thread.hexId == null && hexId != null) {
    // Backfill a hexId that we now know on an already-optimistic thread.
    thread = { ...thread, hexId };
    state.agentChatThreadsMap[threadId] = thread;
  }

  return thread;
};

/**
 * Sets messages for a thread in state.
 * Deduplicates messages by staticId, keeping the most recent for streaming updates.
 */
function setThreadMessages(
  state: AgentChatState,
  {
    currentUserId,
    messages,
    threadId,
  }: {
    threadId: AgentChatThreadId;
    messages: readonly AgentChatMessageFragment[];
    currentUserId: UserId | null;
  },
): void {
  const thread = ensureThreadExists(state, threadId, currentUserId);
  const copy = { ...thread };

  const newMessages = messages.map(messageFragmentToAgentChatMessage);
  copy.agentMessages = mergeAgentChatMessagesWithRevisions(
    copy.agentMessages,
    newMessages,
  );
  copy.messageCount = Math.max(copy.messageCount, copy.agentMessages.length);

  state.agentChatThreadsMap[threadId] = copy;
}
